import { useEffect, useId, useRef, useState } from 'react'
import {
  GEOCODE,
  LOCATION_SEARCH,
  type LocationSelection,
  type LocationSuggestion,
} from '../constants/location'
import { searchLocations, toLocationSelection } from '../helpers/locations-api'

type UseLocationAutocompleteArgs = {
  enabled: boolean
  onSelect: (place: LocationSelection) => void
}

export function useLocationAutocomplete({
  enabled,
  onSelect,
}: UseLocationAutocompleteArgs) {
  const inputId = useId()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const onSelectRef = useRef(onSelect)
  const requestId = useRef(0)

  useEffect(() => {
    onSelectRef.current = onSelect
  }, [onSelect])

  useEffect(() => {
    if (!enabled) {
      requestId.current += 1
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      setError('')
      return
    }

    const normalized = query.trim()
    if (normalized.length < GEOCODE.minQueryLength) {
      requestId.current += 1
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      setError('')
      return
    }

    const id = ++requestId.current
    setLoading(true)
    setError('')

    const timer = window.setTimeout(() => {
      void searchLocations(normalized)
        .then((results) => {
          if (requestId.current !== id) return
          setSuggestions(results)
          setOpen(results.length > 0)
          setError(results.length === 0 ? LOCATION_SEARCH.empty : '')
        })
        .catch(() => {
          if (requestId.current !== id) return
          setSuggestions([])
          setOpen(false)
          setError(LOCATION_SEARCH.failed)
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false)
        })
    }, GEOCODE.searchDebounceMs)

    return () => {
      window.clearTimeout(timer)
    }
  }, [query, enabled])

  function selectSuggestion(suggestion: LocationSuggestion) {
    setOpen(false)
    setQuery(suggestion.label)
    onSelectRef.current(toLocationSelection(suggestion))
    setError('')
  }

  return {
    inputId,
    listId,
    query,
    setQuery,
    suggestions,
    open,
    setOpen,
    loading,
    error,
    selectSuggestion,
  }
}
